import pickElements from "utils/base/pickElements";

/**
 * Determines the proper "interval" for `pickElement` based on a target size.
 */
const calcPickElementInterval = (arrayLength: number, targetSize: number): number => {
  // Handle edge cases
  if (targetSize >= arrayLength) return 1;

  // account for first element
  const remainingSlots = targetSize - 1;

  // Calculate how many internal elements we need to skip to hit our target
  // We subtract 2 from arrayLength to account for first/last elements
  const step = Math.ceil((arrayLength - 2) / remainingSlots);

  return step;
};

/**
 * Picks elements from the provided array returning a new array with `targetSize` elements (or less).
 * Basically, this tries to pick elements from the array in a way that evenly distributes them,
 * while not exceeding the `targetSize`.
 *
 * This wraps over `pickElements` and calculates an appropriate interval for you based on `targetSize`.
 * This allows you to say "I want an array of size N" and this function will calculate the interval for you.
 * You may get fewer elements than the target size, but you will never get more.
 *
 * @example
 * const arr = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
 * const result = pickElementsByTargetSize(arr, 5); // [1, 3, 5, 7, 9]
 */
const pickElementsByTargetSize = <T>(arr: T[], targetSize: number): T[] => {
  const interval = calcPickElementInterval(arr.length, targetSize);
  return pickElements(arr, interval);
};

export default pickElementsByTargetSize;
